import React, { useMemo, useRef, useState } from "react";
import MaterialReactTable from "material-react-table";
import {
  Paper,
  Box,
  Button,
  MenuItem,
  IconButton,
  Dialog,
} from "@mui/material";
import PageTitle from "../../components/PageTitle/PageTitle";
import {
  Delete as DeleteIcon,
  Edit as EditIcon,
  PersonRemove,
  Visibility as ViewIcon,
} from "@mui/icons-material";
import {
  AddDevice,
  ViewDevice,
  useDissociateFromPatient,
  useGetDevices,
} from "../../components/DeviceComponents";
import {
  LoadingPaper,
  LoadingError,
  EmptyPaper,
} from "../../components/LoadingComponents";
import { getDevicesColumns } from "./DevicesColumns";
import { useLocation } from "react-router";
import { useAuthUser } from "react-auth-kit";

function Devices() {
  const authUser = useAuthUser();
  const canCreateDevice = authUser()?.role === 1;
  // === filter ===
  const filter = useLocation()?.state?.filter || "all";

  // === web queries ===
  const devicesQuery = useGetDevices(filter);
  const dissociateFromPatientMutation = useDissociateFromPatient();

  // === AddDevice modal handling ===
  const [openAddDevice, setOpenAddDevice] = useState(false);
  const handleOpenAddDevice = () => {
    setOpenAddDevice(true);
  };
  const handleCloseAddDevice = () => {
    setOpenAddDevice(false);
  };

  // === ViewDevice modal handling ===
  const [openViewDevice, setOpenViewDevice] = useState(false);
  const [selectedDevice, setSelectedDevice] = useState({});
  const openInEditMode = useRef(false);

  const handleOpenViewDevice = (device_id) => {
    // set patient data
    setSelectedDevice(device_id);
    setOpenViewDevice(true);
  };
  const handleCloseViewDevice = () => {
    setOpenViewDevice(false);
  };

  const isDoctor = authUser()?.role === 3;
  const tableColumns = useMemo(
    () =>
      getDevicesColumns({
        showOrg: !isDoctor,
        showPatient: true,
      }),
    [isDoctor]
  );

  const devices = devicesQuery.data?.devices ?? [];

  return (
    <main style={{ width: "100%" }}>
      <Dialog open={openAddDevice}>
        <AddDevice closeModal={handleCloseAddDevice} />
      </Dialog>

      <Dialog open={openViewDevice} fullScreen>
        <ViewDevice
          device_id={selectedDevice}
          _editMode={openInEditMode.current}
          closeModal={handleCloseViewDevice}
        />
      </Dialog>

      <PageTitle title={`Dispositivi (${filter})`} />
      <Paper
        style={{ minHeight: "fitContent", margin: "3vh", maxWidth: "96%" }}
      >
        {canCreateDevice && (
          <Box>
            <Button
              style={{ margin: "15px" }}
              color="primary"
              onClick={handleOpenAddDevice}
              variant="contained"
            >
              AGGIUNGI DISPOSITIVO
            </Button>
          </Box>
        )}
        {devicesQuery.isLoading ? (
          <LoadingPaper />
        ) : devicesQuery.isError ? (
          <LoadingError error={devicesQuery.error} />
        ) : (
          devicesQuery.isSuccess &&
          (devicesQuery.data.devices.length === 0 ? (
            <EmptyPaper message={"Nessun dispositivo disponibile"} />
          ) : (
            <MaterialReactTable
              columns={tableColumns}
              data={devices}
              initialState={{
                density: "compact",
                showColumnFilters: true,
                columnFilters: [],
              }}
              enableFacetedValues={devices.length > 0}
              enablePagination={true}
              enableBottomToolbar={true}
              enableTopToolbar={false}
              enableSorting={true}
              muiTablePaperProps={{
                elevation: 0,
              }}
              enableColumnResizing
              enableColumnActions={false}
              enableRowSelection={false}
              enableRowActions={true}
              displayColumnDefOptions={{
                "mrt-row-actions": { header: "Azioni" },
              }}
              positionActionsColumn="last"
              renderRowActions={({ row }) => (
                <Box sx={{ display: "flex", flexWrap: "nowrap", gap: "8px" }}>
                  <IconButton
                    key="view"
                    onClick={() => {
                      openInEditMode.current = false;
                      handleOpenViewDevice(row.original.id);
                    }}
                    color="primary"
                  >
                    <ViewIcon />
                  </IconButton>
                  {canCreateDevice && (
                    <IconButton
                      key="edit"
                      onClick={() => {
                        openInEditMode.current = true;
                        handleOpenViewDevice(row.original.id);
                      }}
                      color="warning"
                    >
                      <EditIcon />
                    </IconButton>
                  )}

                  <IconButton
                    color="error"
                    key="delete"
                    disabled={row.original.patient_id === null}
                    onClick={() => {
                      setSelectedDevice(row.original);
                      dissociateFromPatientMutation.mutate(row.original);
                    }}
                  >
                    <PersonRemove />
                  </IconButton>
                </Box>
              )}
            />
          ))
        )}
      </Paper>
    </main>
  );
}

export default Devices;
